/**
 * The chart palette, as the theme's own tokens.
 *
 * `--c-chart-1…4` in `@dru/config/theme.css`: the logo's navy and magenta
 * first, then ochre and sky (29 Oct 2026). Each is a CSS variable, so the
 * charts follow the light/dark toggle, and a series is the same hue on every
 * screen — colour follows the entity, never its row number.
 *
 * Validated as a set with the dataviz validator, all pairs, against the card
 * surface in each theme: light passes every check; dark passes with
 * sky↔magenta in the 6–8 CVD band, legal only with a second encoding — which
 * every chart ships: a legend, a distinct marker per series, direct labels at
 * the line ends and the table view.
 *
 * None of the four is a status colour any more. The series used to be amber,
 * green and red — the console's "due soon", "fit" and "unfit" — so a trend
 * line could be read as a verdict.
 */
export type SeriesKey = 'preEmployment' | 'periodic' | 'other';

export interface Series {
  key: SeriesKey | 'treatments';
  label: string;
  color: string;
  /** The marker, so identity never rests on colour alone. */
  marker: 'circle' | 'square' | 'diamond' | 'triangle';
}

export const SERIES: readonly Series[] = [
  { key: 'preEmployment', label: 'Pre-employment', color: 'var(--c-chart-1, #3F3FBF)', marker: 'circle' },
  { key: 'periodic', label: 'Periodic & annual', color: 'var(--c-chart-2, #D0157F)', marker: 'square' },
  { key: 'other', label: 'Other', color: 'var(--c-chart-3, #C7811A)', marker: 'diamond' },
];

/**
 * The fourth line: treatments given at the OHC — casualty, first aid, OPD.
 * Not an examination, so not in the mix; sky, the fourth series colour, with
 * its own triangle marker, legend entry, end label and table column.
 */
export const TREATMENTS: Series = {
  key: 'treatments', label: 'Treatments', color: 'var(--c-chart-4, #3C93C8)', marker: 'triangle',
};

/** The outcome under the lines: examinations signed off, as a quiet area. */
export const CERTIFIED = {
  key: 'certified' as const,
  label: 'Signed off',
  fill: 'var(--c-neutral-300, #D1D1D1)',
  stroke: 'var(--c-neutral-500, #6F6F6F)',
};

/** A segment that is not a series: companies with nothing in the window. */
export const NONE_COLOR = 'var(--c-neutral-300, #D1D1D1)';

/** Chart chrome: hairlines, axis text, the surface the gaps are cut from. */
export const CHROME = {
  grid: 'var(--c-neutral-200, #E5E5E5)',
  axis: 'var(--c-neutral-300, #D1D1D1)',
  tick: 'var(--c-neutral-500, #6F6F6F)',
  text: 'var(--c-neutral-700, #474747)',
  strong: 'var(--c-neutral-900, #1F1F1F)',
  surface: 'var(--c-surface, #FFFFFF)',
  tooltip: 'var(--c-neutral-50, #F8F9FB)',
};

export function colorOf(key: string): string {
  return SERIES.find((s) => s.key === key)?.color ?? NONE_COLOR;
}
